import { apiJson, apiPostJson, apiPatchJson, apiDelete } from "./api";

export interface WsWorkspace {
  id: string;
  user_id: string;
  name: string;
  intent: string;
  budget_min: number | null;
  budget_max: number | null;
  areas: string;
  priorities: string[];
  timeline: string;
  status: string;
  // Set of unit IDs the user is currently comparing. Driven by the
  // compare picker; PATCHed back when the picker changes so the
  // server-side summary worker knows which set to summarise.
  compare_unit_ids: string[];
  brief_version: number;
  created_at: string;
  updated_at: string;
}

// Offer-history blob for a unit currently being negotiated. Mirrors
// the Claude Design canvas shape verbatim (camelCase) — the field
// is JSONB on the server and opaque to the backend, so frontend +
// DB just need to agree. Keeping camelCase matches the design's
// JSX so component code can stay unchanged.
//
// All fields optional except `asking`. The timeline is built from
// whichever of buyer_offer / seller_counter / buyer_call have been
// recorded; absent steps just don't render.
export interface NegotiationOffer {
  asking: number;
  askingOn?: string;
  buyerOffer?: number;
  buyerOfferOn?: string;
  sellerCounter?: number;
  sellerCounterOn?: string;
  buyerCall?: number;
  buyerCallOn?: string;
  counterDue?: string;
  nextStep?: string;
}

export interface WsUnit {
  id: string;
  property_id: string;
  label: string;
  floor: number | null;
  stack: string;
  facing: string;
  size_sqft: number | null;
  price: number | null;
  psf: number | null;
  maintenance: string;
  tenancy: string;
  tenancy_end: string;
  status: string;
  negotiation?: NegotiationOffer | null;
  created_at: string;
  updated_at: string;
}

// One nearby MRT/LRT station entry on WsProperty.nearby_mrt.
// Populated by the dossier API enricher from the canonical dataengine
// (projects.nearby_mrt). Stable shape across enrichment vintages —
// codes/lines empty when LTA Datamall codes haven't landed yet.
export interface WsNearbyMRT {
  station: string;        // "Tanjong Pagar"
  type: string;           // "mrt_exit" | "lrt_exit"
  codes: string[];        // ["EW15"]; ["EW16","NE3","TE17"] at interchanges
  lines: string[];        // ["EWL"]; ["EWL","NEL","TEL"] at interchanges
  distance_m: number;     // straight-line metres to the closest exit
  walk_min: number;       // distance_m / 80, min 1
  nearest_exit?: string;  // closest exit label, e.g. "A" / "4" / "Exit 3"
}

export interface WsProperty {
  id: string;
  workspace_id: string;
  project_name: string;
  district: string;
  tenure: string;
  top_year: number | null;
  beds: string;
  mrt_desc: string;
  // nearby_mrt is the canonical structured list of nearby stations.
  // Union of top-3 by distance + everything within 1km, deduped per
  // station. Empty array (not absent) when the project is awaiting
  // enrichment or has no stations within range.
  nearby_mrt?: WsNearbyMRT[];
  fit_score: number | null;
  status: string;
  ai_summary: string;
  // summary_generated_at: ISO timestamp of the last successful regen,
  // or null/undefined if never generated.
  summary_generated_at?: string | null;
  // summary_stale: true when the server has marked this property's
  // summary dirty and the worker hasn't caught up yet. Render a
  // subtle "Generating…" hint instead of (potentially outdated)
  // ai_summary text — but it's fine to keep showing the old text
  // beneath the hint.
  summary_stale: boolean;
  pros: string[];
  risks: string[];
  questions: Array<{ q: string; status: string; answer?: string }>;
  livability: { naturalLight?: string; noise?: string; layout?: string; wfh?: string } | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  units?: WsUnit[];
}

export interface WsViewing {
  id: string;
  workspace_id: string;
  property_id: string | null;
  unit_label: string;
  status: string;
  viewing_date: string;
  viewing_time: string;
  agent_name: string;
  checklist: Array<{ item: string; done: boolean }>;
  ratings: { layout: number | null; light: number | null; noise: number | null; condition: number | null } | null;
  tags: string[];
  notes: string;
  ai_summary: string;
  summary_generated_at?: string | null;
  summary_stale: boolean;
  decision: string | null;
  media: Array<{ type: string; label: string; thumb: string; duration?: string }>;
  created_at: string;
  updated_at: string;
}

// Compare summary blurb attached to WsFull. Body is empty until the
// worker has produced one for the current selection. `stale` is true
// when the cached body is for a different selection than the workspace
// currently has, OR the worker is mid-regen.
export interface WsCompareSummary {
  body: string;
  generated_at?: string | null;
  stale: boolean;
  unit_ids_hash: string;
}

export interface WsFull extends WsWorkspace {
  properties: WsProperty[];
  viewings: WsViewing[];
  compare_summary?: WsCompareSummary | null;
}

export async function listWorkspaces(): Promise<WsWorkspace[]> {
  const r = await apiJson<{ workspaces: WsWorkspace[] }>("/workspaces");
  return r.workspaces ?? [];
}

export async function createWorkspace(name: string, intent: string): Promise<WsWorkspace> {
  return apiPostJson<WsWorkspace>("/workspaces", { name, intent });
}

export async function getWorkspaceFull(id: string): Promise<WsFull> {
  return apiJson<WsFull>(`/workspaces/${id}`);
}

export async function addPropertyApi(wsId: string, input: object): Promise<WsProperty> {
  return apiPostJson<WsProperty>(`/workspaces/${wsId}/properties`, input);
}

export async function updatePropertyApi(wsId: string, pid: string, patch: object): Promise<void> {
  await apiPatchJson(`/workspaces/${wsId}/properties/${pid}`, patch);
}

export async function addUnitApi(wsId: string, pid: string, input: object): Promise<WsUnit> {
  return apiPostJson<WsUnit>(`/workspaces/${wsId}/properties/${pid}/units`, input);
}

export async function deleteUnitApi(wsId: string, pid: string, uid: string): Promise<void> {
  await apiDelete(`/workspaces/${wsId}/properties/${pid}/units/${uid}`);
}

export async function updateUnitApi(wsId: string, pid: string, uid: string, patch: object): Promise<void> {
  await apiPatchJson(`/workspaces/${wsId}/properties/${pid}/units/${uid}`, patch);
}

export async function updateWorkspaceApi(id: string, patch: {
  name?: string; intent?: string;
  budget_min?: number | null; budget_max?: number | null;
  areas?: string; priorities?: string[]; timeline?: string;
  // PATCHing a present-but-empty array clears the compare picker.
  // Server interprets null/undefined as "leave unchanged".
  compare_unit_ids?: string[];
}): Promise<void> {
  await apiPatchJson(`/workspaces/${id}`, patch);
}

export async function addViewingApi(wsId: string, input: object): Promise<WsViewing> {
  return apiPostJson<WsViewing>(`/workspaces/${wsId}/viewings`, input);
}

export async function updateViewingApi(wsId: string, vid: string, patch: object): Promise<void> {
  await apiPatchJson(`/workspaces/${wsId}/viewings/${vid}`, patch);
}
